/**
 * Campaign Mailout panel — PostGrid Print & Mail section inside the
 * Property Campaign detail page. Totals, recipient review, previews,
 * test sends, Approve & Send with exact-count confirmation, resend,
 * cancellation and status sync.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Mailbox, RefreshCw, Eye, Send, ShieldAlert, Loader2, XCircle,
  RotateCcw, FlaskConical, ExternalLink,
} from "lucide-react";
import api from "@/lib/api";
import PostcardLiveSendConfirm from "@/components/admin/PostcardLiveSendConfirm";
import { Button } from "@/components/ui/button";

const card = "rounded-2xl bg-white border border-slate-200 p-5";

const BUCKET_STYLES = {
  ready: "bg-cyan-50 text-cyan-800 border border-cyan-200",
  already_mailed: "bg-emerald-50 text-emerald-800 border border-emerald-200",
  test_mailed: "bg-amber-50 text-amber-800 border border-amber-300",
  excluded: "bg-slate-100 text-slate-500 border border-slate-200",
  error: "bg-rose-50 text-rose-700 border border-rose-200",
};
const BUCKET_LABELS = {
  ready: "Ready to mail",
  already_mailed: "Mailed",
  test_mailed: "TEST — NOT PHYSICALLY MAILED",
  excluded: "Excluded",
  error: "Invalid / error",
};
const PG_STATUS_LABELS = {
  ready: "Ready (queued at PostGrid)",
  printing: "Printing",
  processed_for_delivery: "Processed for delivery",
  completed: "Mail process completed / estimated delivered",
  cancelled: "Cancelled",
  failed: "Submission failed",
};
const BING_STATUS_LABELS = {
  submitted: "Submitted to Bing",
  received: "Received by Bing",
  lodged: "Lodged with Australia Post",
  returned: "Returned to sender",
  cancelled: "Cancelled",
  unknown: "Status unknown",
};

function Stat({ label, value, tone, tid }) {
  return (
    <div data-testid={tid} className={`rounded-xl border px-3 py-2 ${tone || "bg-white border-slate-200"}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

export default function CampaignMailout({ campaignId, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(null); // {count}
  const [liveConfirm, setLiveConfirm] = useState(null); // postcard LIVE gate
  const [testConfirm, setTestConfirm] = useState(null); // recipient row
  const [proofs, setProofs] = useState(null);
  const [proofsBusy, setProofsBusy] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);

  const load = useCallback((sync = false) => {
    api.get(`/admin/property-campaigns/${campaignId}/mailout${sync ? "?sync=1" : ""}`)
      .then(({ data: d }) => setData(d))
      .catch(() => {});
  }, [campaignId]);
  useEffect(() => load(true), [load]);

  if (!data) return null;
  const { counts, recipients, mode, status_totals, unit_cost_aud } = data;
  const provider = data.provider || "postgrid";
  const isBing = provider === "bing";
  const providerName = isBing ? "Bing Mail" : "PostGrid";
  const providerOk = data.provider_configured ?? (data.keys_configured || {})[mode];
  const live = mode === "live";
  const readyCount = counts.ready + counts.error;
  const fmtCost = (n) => `$${(n * (unit_cost_aud || 0)).toFixed(2)}`;

  const previewLetter = async (iid, side) => {
    try {
      const token = localStorage.getItem("scwt_token");
      const qs = side ? `?side=${side}` : "";
      const r = await fetch(`${api.defaults.baseURL}/admin/property-campaigns/${campaignId}/mailout/preview/${iid}${qs}`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) {
        let detail = "";
        try { detail = (await r.json()).detail; } catch { /* non-JSON */ }
        throw new Error(detail || "");
      }
      const url = URL.createObjectURL(await r.blob());
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e.message || "Preview failed"); }
  };

  // Proof grid — random sample fully rendered before LIVE approval.
  const loadProofs = async (randomise = false) => {
    setProofsBusy(true);
    try {
      const { data: r } = await api.get(
        `/admin/property-campaigns/${campaignId}/mailout/postcard-proofs`,
        { params: { sample: 10, seed: randomise ? Date.now() % 100000 : 0 } });
      setProofs(r);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not build proofs"); }
    finally { setProofsBusy(false); }
  };

  const previewSample = () => {
    const first = recipients.find((r) => r.bucket === "ready") || recipients[0];
    if (first) previewLetter(first.id);
  };

  const postgridPreview = async (iid) => {
    try {
      const { data: r } = await api.get(`/admin/property-campaigns/${campaignId}/mailout/letter-url/${iid}`);
      window.open(r.url, "_blank");
    } catch (e) { toast.error(e.response?.data?.detail || "PostGrid preview not available yet"); }
  };

  const sendTest = async () => {
    if (!testConfirm) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/property-campaigns/${campaignId}/mailout/test-letter`, { invite_id: testConfirm.id });
      toast.success(isBing
        ? `Test ${Piece.toLowerCase()} submitted to Bing (HELD — ref ${r.reference}). Recipient remains Ready to Mail — NOT marked as mailed.`
        : `Test ${Piece.toLowerCase()} created at PostGrid (${r.letter_id}) — NOT physically mailed`);
      setTestConfirm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Test letter failed"); }
    finally { setBusy(false); }
  };

  const approveSend = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/property-campaigns/${campaignId}/mailout/send`, {
        confirm_count: confirming.count,
        ...(live ? { confirm_live: true } : {}),
      });
      if (r.failed > 0) {
        toast.warning(`${r.submitted} submitted, ${r.failed} failed — see recipient list for errors`);
      } else {
        toast.success(live
          ? `${r.submitted} ${pieces} submitted to ${providerName} for printing & postage`
          : `${r.submitted} TEST ${pieces} submitted — not physically mailed`);
      }
      setConfirming(null); load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Mailout failed"); }
    finally { setBusy(false); }
  };

  const resend = async (rec) => {
    if (isPostcard && live) {
      // any real postcard passes the mandatory LIVE SEND gate
      setLiveConfirm({ kind: "single", inviteId: rec.id });
      return;
    }
    if (!window.confirm(`Intentionally RESEND the letter for ${rec.street_address}? A new letter with a new idempotency key will be created${live ? " and physically mailed (real charges)" : " in TEST mode"}.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/property-campaigns/${campaignId}/mailout/resend/${rec.id}`,
        live ? { confirm_live: true } : {});
      toast.success("Resend submitted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Resend failed"); }
    finally { setBusy(false); }
  };

  const cancel = async (rec) => {
    if (!window.confirm(`Ask PostGrid to cancel the letter for ${rec.street_address}? Only possible while it is still queued.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/property-campaigns/${campaignId}/mailout/cancel/${rec.id}`);
      toast.success("Letter cancelled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancellation refused"); }
    finally { setBusy(false); }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/property-campaigns/${campaignId}/mailout/sync`);
      toast.success(`Statuses synced — ${r.changed} changed of ${r.checked} checked`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setBusy(false); }
  };

  // Sep 2026 — postcards are a second FORMAT on the same Bing mailout,
  // so every label is derived from one place rather than forking the UI.
  const isPostcard = data.mail_format === "postcard";
  const studio = data.postcard_studio;
  const usesStudio = !!studio?.uses_studio;
  const Piece = isPostcard ? "Postcard" : "Letter";
  const pieces = isPostcard ? "postcards" : "letters";

  return (
    <div className={`${card} mt-6`} data-testid="pc-mailout-panel">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wider"><Mailbox className="w-4 h-4 inline mr-1" /> Mailout — {providerName}</h2>
        <span data-testid="pc-mailout-mode" className={`text-[11px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1 ${live ? "bg-rose-600 text-white" : "bg-amber-400 text-amber-950"}`}>
          {live ? "LIVE MODE — REAL MAIL" : isBing ? "TEST MODE — submissions are HELD at Bing, nothing prints until released" : "TEST MODE — nothing is physically mailed"}
        </span>
        <a href={isBing ? "/admin/settings/bing-mail" : "/admin/settings/postgrid"} className="text-xs text-cyan-700 font-semibold underline" data-testid="pc-mailout-settings-link">{providerName} settings</a>
        <span className="ml-auto flex gap-2">
          <Button data-testid="pc-mailout-sync" size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={syncNow}><RefreshCw className="w-3.5 h-3.5 mr-1" /> {isBing ? "Sync Bing Status" : "Sync statuses"}</Button>
          <Button data-testid="pc-mailout-preview-sample" size="sm" variant="outline" className="rounded-full" disabled={!recipients.length} onClick={previewSample}><Eye className="w-3.5 h-3.5 mr-1" /> Preview {isPostcard ? "postcard" : "sample"}</Button>
          <Button data-testid="pc-mailout-send-test-letter" size="sm" variant="outline"
            className="rounded-full border-amber-400 text-amber-800 hover:bg-amber-50 hover:text-amber-900"
            disabled={busy || readyCount === 0 || !providerOk}
            onClick={() => setTestConfirm(recipients.find((r) => r.bucket === "ready") || recipients.find((r) => r.bucket === "error") || null)}>
            <FlaskConical className="w-3.5 h-3.5 mr-1" /> Send Test {Piece}
          </Button>
          <Button data-testid="pc-mailout-approve" size="sm" className={`rounded-full ${live ? "bg-rose-600 hover:bg-rose-700" : "bg-amber-600 hover:bg-amber-700"}`}
            disabled={busy || readyCount === 0 || !providerOk}
            onClick={() => (isPostcard && live
              ? setLiveConfirm({ kind: "batch" })
              : setConfirming({ count: readyCount }))}>
            <Send className="w-3.5 h-3.5 mr-1" /> Approve &amp; Send ({readyCount})
          </Button>
        </span>
      </div>

      {isPostcard && (
        <div className="mb-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-700" data-testid="pc-postal-area-note">
          <b>Postal address is rendered by Bing in the reserved postal area</b> on the
          right-hand side of the reverse. Our artwork stays inside the left
          116 × 126 mm only — the Front and Reverse previews show exactly what we
          supply, so the address block is deliberately absent from them.
        </div>
      )}

      {isPostcard && usesStudio && (
        <div className="mb-3 rounded-xl border border-cyan-200 bg-cyan-50/60 p-3 text-xs text-cyan-900" data-testid="pc-mailout-studio-note">
          Artwork source: <b>{studio.assignment_label}</b>. Test and LIVE sends upload the
          exact generated snapshot for each recipient — review proofs in Postcard Studio
          and use <b>Generate Postcards</b> above before sending.
          {" "}<b>{studio.counts?.generated || 0}</b> generated
          {(studio.counts?.requires_review || 0) > 0 && <> · <b className="text-rose-700">{studio.counts.requires_review} require review</b></>}
          {(studio.counts?.failed || 0) > 0 && <> · <b className="text-rose-700">{studio.counts.failed} failed</b></>}
        </div>
      )}

      {isPostcard && !usesStudio && (
        <div className="mb-3 rounded-xl border border-cyan-200 bg-cyan-50/60 p-3" data-testid="pc-mailout-proofs">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wider text-cyan-900">
              Postcard proofs — {proofs ? `${proofs.sample} of ${proofs.pool_size}` : "review before approving"}
            </span>
            <span className="text-[11px] text-slate-600">
              Front {proofs?.front_template_version || "PWS_POSTCARD_FRONT_V1"} · Reverse {proofs?.back_template_version || "PWS_POSTCARD_BACK_V1"}
            </span>
            <span className="ml-auto flex gap-2">
              <Button data-testid="pc-mailout-proofs-load" size="sm" variant="outline" className="rounded-full" disabled={proofsBusy} onClick={() => loadProofs(false)}>
                {proofsBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1" />} Review Postcard Proofs
              </Button>
              {proofs && (
                <Button data-testid="pc-mailout-proofs-randomise" size="sm" variant="outline" className="rounded-full" disabled={proofsBusy} onClick={() => loadProofs(true)}>
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Randomise
                </Button>
              )}
            </span>
          </div>
          {proofs && (
            <>
              <p className="mt-2 text-[11px] text-slate-600">{proofs.postal_note}</p>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                {proofs.proofs.map((pf) => (
                  <div key={pf.invite_id} className={`rounded-lg border p-2.5 bg-white ${pf.preflight_ok ? "border-slate-200" : "border-rose-300"}`} data-testid={`pc-proof-${pf.invite_id}`}>
                    <div className="text-[11px] font-bold text-slate-900">{pf.property_address}</div>
                    <div className="text-[10px] text-slate-500">Artwork address: {pf.artwork_address || "—"}</div>
                    <div className="mt-1 text-[10px] font-semibold text-slate-700 leading-snug">{pf.headline || pf.requires_review}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px]">
                      <span className="text-slate-500">QR {pf.qr_token}</span>
                      {pf.headline_font_pt && <span className="text-slate-500">{pf.headline_font_pt}pt · {pf.headline_lines?.length} lines</span>}
                      <span className={pf.preflight_ok ? "text-emerald-700 font-semibold" : "text-rose-700 font-semibold"}>
                        {pf.preflight_ok ? "Preflight OK" : `Requires review: ${pf.preflight_problems?.[0] || pf.requires_review}`}
                      </span>
                    </div>
                    <div className="mt-1.5 flex gap-2">
                      <button onClick={() => previewLetter(pf.invite_id, "front")} data-testid={`pc-proof-front-${pf.invite_id}`} className="text-[11px] font-semibold text-cyan-700 hover:text-cyan-900">Front</button>
                      <button onClick={() => previewLetter(pf.invite_id, "back")} data-testid={`pc-proof-back-${pf.invite_id}`} className="text-[11px] font-semibold text-cyan-700 hover:text-cyan-900">Reverse</button>
                      <span className="text-[10px] text-slate-400 ml-auto">Postal: {pf.postal_lines?.join(" · ")}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <div className="mb-3 rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-xs font-semibold text-sky-800" data-testid="pc-mailout-batch-note">
        <b>Approve &amp; Send applies to ALL Ready-to-Mail recipients</b> — the checkboxes in the recipient list do not control the batch. To safely preview one physical {pieces.slice(0, -1)} first, use <b>Send Test {Piece}</b> on a single recipient (held at Bing, never marked as mailed).
      </div>

      {!providerOk && (
        <div className="mb-3 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700" data-testid="pc-mailout-nokey">
          {providerName} credentials are not configured — add them in the server environment, then re-check in {providerName} settings.
        </div>
      )}

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2.5">
        <Stat tid="pc-mailout-total" label="Total properties" value={counts.total} />
        <Stat tid="pc-mailout-ready" label="Ready to mail" value={counts.ready} tone="bg-cyan-50 border-cyan-200" />
        <Stat tid="pc-mailout-mailed" label="Already mailed" value={counts.already_mailed} tone="bg-emerald-50 border-emerald-200" />
        <Stat tid="pc-mailout-test" label={`Test ${pieces}`} value={counts.test_mailed + (data.test_letters_sent || 0)} tone="bg-amber-50 border-amber-200" />
        <Stat tid="pc-mailout-excluded" label="Excluded" value={counts.excluded} />
        <Stat tid="pc-mailout-error" label="Invalid / error" value={counts.error} tone={counts.error ? "bg-rose-50 border-rose-200" : undefined} />
        {(counts.blocked_b_draft || 0) > 0 && (
          <Stat tid="pc-mailout-blocked-b" label="Ver B blocked (draft)" value={counts.blocked_b_draft} tone="bg-violet-50 border-violet-200" />
        )}
      </div>

      {unit_cost_aud > 0 && readyCount > 0 && (
        <div className="mt-3 text-xs font-semibold text-slate-600" data-testid="pc-mailout-cost-estimate">
          Estimated cost to mail the {readyCount} ready propert{readyCount === 1 ? "y" : "ies"}: <b className="text-slate-900">{fmtCost(readyCount)}</b> ({readyCount} × ${Number(unit_cost_aud).toFixed(2)} print + postage)
        </div>
      )}

      {Object.keys(status_totals).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="pc-mailout-status-totals">
          {Object.entries(status_totals).map(([k, v]) => {
            const [m, st] = k.split(":");
            const bing = m.startsWith("bing-");
            const labels = bing ? BING_STATUS_LABELS : PG_STATUS_LABELS;
            const mo = bing ? m.replace("bing-", "") : m;
            return (
              <span key={k} className={`text-[11px] font-semibold rounded-full px-2.5 py-1 ${mo === "test" ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-slate-100 text-slate-700 border border-slate-200"}`}>
                {bing ? "BING " : "POSTGRID "}{mo.toUpperCase()} · {labels[st] || st}: <b>{v}</b>
              </span>
            );
          })}
        </div>
      )}

      <button data-testid="pc-mailout-toggle-recipients" onClick={() => setShowRecipients(!showRecipients)} className="mt-4 text-xs font-bold text-cyan-700 underline">
        {showRecipients ? "Hide recipient review" : `Review all recipients (${recipients.length})`}
      </button>

      {showRecipients && (
        <div className="mt-3 overflow-x-auto" data-testid="pc-mailout-recipients">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
              <th className="py-2 pr-3">Property</th><th className="pr-3">Mailout status</th><th className="pr-3">{isBing ? "Bing Mailing" : "PostGrid"}</th><th className="pr-3">Actions</th>
            </tr></thead>
            <tbody>
              {recipients.map((r) => {
                const pg = r.postgrid;
                const bg = r.bing;
                const cancellable = !bg && pg?.letter_id && pg.status === "ready";
                return (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`pc-mailout-row-${r.id}`}>
                    <td className="py-2 pr-3 font-semibold text-slate-900">{r.street_address}, {r.suburb} {r.state} {r.postcode}</td>
                    <td className="pr-3">
                      <span data-testid={`pc-mailout-bucket-${r.id}`} className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${BUCKET_STYLES[r.bucket]}`}>{BUCKET_LABELS[r.bucket]}</span>
                    </td>
                    <td className="pr-3 text-xs text-slate-600" data-testid={`pc-mailout-pg-${r.id}`}>
                      {bg ? (
                        <>
                          <b className={bg.state === "failed" ? "text-rose-600" : "text-slate-800"}>
                            {bg.state === "failed" ? "Submission failed" : BING_STATUS_LABELS[bg.status] || bg.status}
                          </b>
                          {bg.cust_hold && bg.state === "submitted" && <span className="ml-1 text-amber-700 font-bold">(HELD — release in Bing portal)</span>}
                          {bg.attempt > 1 && <span className="ml-1 text-violet-700 font-bold">(attempt #{bg.attempt})</span>}
                          <div className="font-mono text-[10px] text-slate-400">
                            Bing Ref {bg.reference}{bg.epid ? ` · EPID ${bg.epid}` : ""}{!bg.live && " · TEST"}
                          </div>
                          {bg.last_status_at && <div className="text-[10px] text-slate-400">Last sync {new Date(bg.last_status_at).toLocaleString("en-AU")}</div>}
                          {bg.error && <div className="text-rose-600">{bg.error}</div>}
                        </>
                      ) : pg?.letter_id ? (
                        <>
                          <b className={pg.status === "failed" ? "text-rose-600" : "text-slate-800"}>{PG_STATUS_LABELS[pg.status] || pg.status}</b>
                          {pg.resend_seq > 0 && <span className="ml-1 text-violet-700 font-bold">(resend #{pg.resend_seq})</span>}
                          <div className="font-mono text-[10px] text-slate-400">PostGrid {pg.letter_id}{!pg.live && " · TEST"}</div>
                        </>
                      ) : pg?.error ? <span className="text-rose-600">{pg.error}</span> : "—"}
                      {r.bing_test && (
                        <div data-testid={`pc-mailout-testinfo-${r.id}`} className="mt-0.5 text-[10px] font-semibold text-amber-700">
                          {r.bing_test.state === "failed"
                            ? <>Test letter attempt failed: {r.bing_test.error}</>
                            : <>Test {Piece.toLowerCase()} #{r.test_letters_sent || r.bing_test.attempt} held at Bing · not counted as mailed</>}
                          <div className="font-mono text-slate-400">{r.bing_test.reference}</div>
                        </div>
                      )}
                    </td>
                    <td className="pr-3">
                      <span className="flex flex-wrap gap-2">
                        {isPostcard ? (
                          <>
                            <button data-testid={`pc-mailout-preview-front-${r.id}`} onClick={() => previewLetter(r.id, "front")} className="text-xs font-semibold text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-0.5"><Eye className="w-3 h-3" /> Front</button>
                            <button data-testid={`pc-mailout-preview-back-${r.id}`} onClick={() => previewLetter(r.id, "back")} className="text-xs font-semibold text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-0.5 ml-2"><Eye className="w-3 h-3" /> Reverse</button>
                            <span className="block text-[10px] text-slate-500 mt-0.5" data-testid={`pc-mailout-postal-${r.id}`} title="Rendered by Bing in the reserved postal area">
                              Postal: {r.street_address} · {r.suburb} {r.state} {r.postcode}
                            </span>
                          </>
                        ) : (
                          <button data-testid={`pc-mailout-preview-${r.id}`} onClick={() => previewLetter(r.id)} className="text-xs font-semibold text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-0.5"><Eye className="w-3 h-3" /> Preview</button>
                        )}
                        {pg?.letter_id && !bg && (
                          <button data-testid={`pc-mailout-pgview-${r.id}`} onClick={() => postgridPreview(r.id)} className="text-xs font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-0.5"><ExternalLink className="w-3 h-3" /> PostGrid proof</button>
                        )}
                        {(r.bucket === "ready" || r.bucket === "error") && (
                          <button data-testid={`pc-mailout-test-${r.id}`} disabled={busy} onClick={() => setTestConfirm(r)} className="text-xs font-semibold text-amber-700 hover:text-amber-900 inline-flex items-center gap-0.5"><FlaskConical className="w-3 h-3" /> Send test letter</button>
                        )}
                        {(bg?.epid || (pg?.letter_id && pg.status !== "cancelled")) && (
                          <button data-testid={`pc-mailout-resend-${r.id}`} disabled={busy} onClick={() => resend(r)} className="text-xs font-semibold text-violet-700 hover:text-violet-900 inline-flex items-center gap-0.5"><RotateCcw className="w-3 h-3" /> Resend</button>
                        )}
                        {cancellable && (
                          <button data-testid={`pc-mailout-cancel-${r.id}`} disabled={busy} onClick={() => cancel(r)} className="text-xs font-semibold text-rose-600 hover:text-rose-800 inline-flex items-center gap-0.5"><XCircle className="w-3 h-3" /> Cancel</button>
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {testConfirm && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="pc-test-letter-modal">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
            <div className="flex items-center gap-2 mb-3">
              <FlaskConical className="w-6 h-6 text-amber-600" />
              <h3 className="font-bold text-lg text-slate-900">Send ONE Test Letter</h3>
            </div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Recipient (choose exactly one)</label>
            <select data-testid="pc-test-letter-recipient-select" value={testConfirm.id} disabled={busy}
              onChange={(e) => setTestConfirm(recipients.find((t) => t.id === e.target.value) || testConfirm)}
              className="w-full mb-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-900">
              {recipients.filter((t) => t.bucket === "ready" || t.bucket === "error").map((t) => (
                <option key={t.id} value={t.id}>{t.street_address}, {t.suburb} {t.state} {t.postcode}</option>
              ))}
            </select>
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-900" data-testid="pc-test-letter-address">
              {testConfirm.street_address}, {testConfirm.suburb} {testConfirm.state} {testConfirm.postcode}
            </div>
            <ul className="mt-3 space-y-1.5 text-sm text-slate-700" data-testid="pc-test-letter-rules">
              <li>• This is a <b className="text-amber-700">TEST MODE</b> submission of this recipient&apos;s existing generated letter{isBing ? " — it is HELD at Bing and nothing prints or posts unless you release it in the Bing Customer Portal" : " to PostGrid's sandbox"}.</li>
              <li>• It will <b>NOT</b> mark the recipient as mailed and will <b>NOT</b> change &quot;Already Mailed&quot;, the QR code, the letter reference or campaign analytics.</li>
              <li>• The recipient <b>remains in the Ready-to-Mail live batch</b> and stays fully eligible for the later live send.</li>
              <li>• Only the separate <b>Test {Piece}s</b> counter increases, and the send is audit-logged as a Test {Piece}.</li>
            </ul>
            <div className="mt-5 flex justify-end gap-2">
              <Button data-testid="pc-test-letter-cancel" variant="outline" className="rounded-full" onClick={() => setTestConfirm(null)} disabled={busy}>Cancel</Button>
              <Button data-testid="pc-test-letter-confirm" className="rounded-full bg-amber-600 hover:bg-amber-700" onClick={sendTest} disabled={busy}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit 1 TEST letter"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {confirming && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="pc-mailout-confirm-dialog">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert className={`w-6 h-6 ${live ? "text-rose-600" : "text-amber-600"}`} />
              <h3 className="font-bold text-lg text-slate-900">{live ? "Confirm LIVE mailout" : "Confirm TEST mailout"}</h3>
            </div>
            {live ? (
              <p className="text-sm text-slate-700" data-testid="pc-mailout-confirm-text">
                You are about to submit <b className="text-rose-700">{confirming.count} physical {pieces}</b> to {providerName} for printing and postage. <b>This will incur real charges.</b>
                {isBing && <> Live packs may be <b>held for manual release</b> in the Bing Customer Portal depending on your Bing Mail settings.</>}
                {unit_cost_aud ? <> Estimated cost: <b className="text-rose-700" data-testid="pc-mailout-confirm-cost">{fmtCost(confirming.count)}</b> ({confirming.count} × ${Number(unit_cost_aud).toFixed(2)}).</> : <> Set a cost per letter in settings to see the estimated batch cost here.</>} Continue?
              </p>
            ) : (
              <p className="text-sm text-slate-700" data-testid="pc-mailout-confirm-text">
                {isBing
                  ? <>You are about to submit <b>{confirming.count} TEST {pieces}</b> to Bing Mail with <b>customer hold</b> — they will wait in the Bing Customer Portal and nothing prints or posts unless you manually release them there.</>
                  : <>You are about to submit <b>{confirming.count} TEST {pieces}</b> to PostGrid's sandbox. They will be labelled <b>TEST — NOT PHYSICALLY MAILED</b> and no real mail or charges will occur.</>}
                {unit_cost_aud ? <> A live batch of this size would cost about <b data-testid="pc-mailout-confirm-cost">{fmtCost(confirming.count)}</b>.</> : null}
              </p>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <Button data-testid="pc-mailout-confirm-cancel" variant="outline" className="rounded-full" onClick={() => setConfirming(null)} disabled={busy}>Cancel</Button>
              <Button data-testid="pc-mailout-confirm-send" className={`rounded-full ${live ? "bg-rose-600 hover:bg-rose-700" : "bg-amber-600 hover:bg-amber-700"}`} onClick={approveSend} disabled={busy}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Submit ${confirming.count} ${pieces}`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {liveConfirm && (
        <PostcardLiveSendConfirm campaignId={campaignId} kind={liveConfirm.kind}
          inviteId={liveConfirm.inviteId} onClose={() => setLiveConfirm(null)}
          onSent={() => { load(); onChanged?.(); }} />
      )}
    </div>
  );
}
